import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material";
import { StewartDialog } from "controls/global/dialogs/stewart-dialog/StewartDialog";
import CloseIcon from "@mui/icons-material/Close";
import CalculateIcon from "@mui/icons-material/Calculate";
import {
  borderRadius,
  colors,
  fontSize,
  fontWeight,
  gaps,
  iconSize,
  margin,
  padding,
} from "theme/defaultStyle";
import ActionButton from "controls/global/action-button/ActionButton";
import { PolicyManagementRecord } from "entities/UIModel/PolicyManagementRecord";
import { Info, PublishedWithChanges } from "@mui/icons-material";
import CheckboxInput from "controls/global/checkbox-field/CheckboxInput";
import { showToast } from "controls/global/stewart-toast";
import { bulkAdjustPolicyHistory } from "utils/api/policyHistoryApi";
// The repo-wide formatter, aliased to the name this file already used.
import { formatCurrency as fmtCurrency } from "utils/shared";
import {
  bookedPremium,
  bookedRemittance,
  expectedRemittance,
  fmtPct,
  isSplitMismatch,
  REMITTANCE_OVER_PREMIUM_FIELD_ERROR,
  SPLIT_MISMATCH_FIELD_ERROR,
  toNumOrNull,
} from "./helper";
import PercentNumberFormat from "../shared/PercentNumberFormat";

const BULK_ADJUST_SUCCESS_SUFFIX =
  "adjusted. The reversal and replacement records will appear in the Close Queue.";
const BULK_ADJUST_ERROR =
  "Failed to apply the bulk adjustment. Please try again.";
// Rows without a PolicyHistoryID can't be sent, so a batch of only those has
// nothing to submit even though Apply looked enabled.
const BULK_ADJUST_NO_ROWS = "No records in this batch can be adjusted.";

const StyledDialog = styled(StewartDialog)({
  "& > .MuiDialog-container > .MuiDialog-paper": {
    maxHeight: "calc(95vh)",
    maxWidth: "calc(90vw)",
    minWidth: "calc(90vw)",
    display: "flex",
    flexDirection: "column",
    padding: `${padding.xlarge21_5} ${padding.xlarge22} ${padding.xlarge22} ${padding.xlarge22}`,
    overflow: "hidden",
  },
});

const StyledDialogTitle = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  columnGap: gaps.large1,
  "& .MuiTypography-root": {
    fontSize: fontSize.xlarge2,
    fontWeight: fontWeight.bold2,
  },
});

const IconContainer = styled("div")({
  background: colors.blue19,
  borderRadius: borderRadius.large,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "56px",
  height: "56px",
  "& .MuiSvgIcon-root": {
    color: colors.blue15,
    fontSize: iconSize.medium2,
  },
});

const CloseIconContainer = styled("div")({
  background: colors.grey10,
  borderRadius: borderRadius.xlarge1,
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  marginLeft: "auto",
  marginBottom: "auto",
  width: "32px",
  height: "32px",
  padding: padding.xsmall1,
  "& .MuiSvgIcon-root": {
    color: colors.blue01,
    width: "24px",
    height: "24px",
  },
  "& .iconButton": {
    padding: padding.zero,
    backgroundColor: colors.grey10,
  },
});

const StyledDialogContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.large1,
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
});

const Subtitle = styled(Typography)({
  fontSize: fontSize.small,
  color: colors.grey11,
});

const DialogFooter = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: gaps.small1,
  marginTop: margin.large,
  paddingTop: padding.large1,
  borderTop: `1px solid ${colors.grey09}`,
});

const NoteSection = styled(Box)({
  marginTop: gaps.small3,
});

const NoteHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  marginBottom: gaps.xsmall2,
});

const InfoBanner = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  gap: gaps.small1,
  marginBottom: gaps.medium1,
  padding: `${padding.small1} ${padding.medium1}`,
  backgroundColor: colors.blue20,
  border: `1px solid ${colors.blue21}`,
  borderRadius: borderRadius.small,
  color: colors.grey11,
});

const CalcToolbar = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: gaps.small1,
  background: colors.blue20,
  border: `1px solid ${colors.blue21}`,
  borderRadius: borderRadius.small,
  padding: `${padding.small1} ${padding.medium1}`,
  marginBottom: gaps.small1,
});

const CalcSection = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: gaps.small1,
});

const CalcLabel = styled(Typography)({
  fontSize: fontSize.xsmall,
  fontWeight: fontWeight.bold2,
  color: colors.blue15,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
});

const CalcHint = styled(Typography)<{ component?: React.ElementType }>({
  fontSize: fontSize.small,
  color: colors.grey11,
});

const SplitsTableWrap = styled(Box)({
  border: `1px solid ${colors.grey09}`,
  borderRadius: borderRadius.small,
  overflow: "auto",
  maxHeight: "45vh",
});

const SplitsTable = styled("table")({
  width: "100%",
  borderCollapse: "collapse",
  fontSize: fontSize.xsmall,
});

const Th = styled("th")({
  textAlign: "left",
  padding: `${padding.small1}`,
  fontWeight: fontWeight.bold2,
  color: colors.grey11,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  background: colors.grey10,
});

const ThNew = styled(Th)({
  color: colors.blue15,
  background: colors.blue19,
});

const ThRev = styled(Th)({
  textAlign: "center",
  color: colors.yellow01,
  background: colors.yellow04,
});

const Td = styled("td")({
  padding: `${padding.xsmall2} ${padding.small1}`,
  color: colors.black,
  whiteSpace: "nowrap",
  borderTop: `1px solid ${colors.grey09}`,
});

const TdNum = styled(Td)({
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
  verticalAlign: "top",
});

const ReversalPreview = styled("div")({
  color: colors.yellow01,
  fontStyle: "italic",
  marginTop: "1px",
});

const TdNew = styled(Td)({
  background: colors.blue19,
  verticalAlign: "top",
});

const TdRev = styled(Td)({
  background: colors.yellow04,
  textAlign: "center",
  "& .checkboxWrapper": { justifyContent: "center" },
});

const EmptySplits = styled(Box)({
  padding: padding.medium1,
  textAlign: "center",
  fontSize: fontSize.small,
  color: colors.grey11,
});

const NumericInput = styled(TextField)({
  background: colors.white,
  "& .MuiInputBase-input": { textAlign: "right", padding: "4px 6px" },
});

const SplitPctInput = styled(NumericInput)({ width: "100px" });

const MoneyInput = styled(NumericInput)({ width: "110px" });

// Batch-level validation summary in the footer, next to the action buttons.
const FooterError = styled("div")({
  flex: 1,
  paddingLeft: padding.medium1,
  color: colors.red02,
  fontSize: fontSize.small,
});

// Per-row validation message, shown under the offending New UW Remittance.
const CellError = styled("div")({
  color: colors.red02,
  fontSize: fontSize.xxsmall,
  fontStyle: "italic",
  marginTop: "2px",
  whiteSpace: "normal",
  maxWidth: "110px",
});

type MoneyField = "newLiability" | "newPremium" | "newRemittence";

type SplitRow = {
  id: string;
  policyHistoryId: number;
  policy: string;
  period: string;
  booked: string;
  originalLiability: number;
  originalPremium: number;
  originalUWRemittence: number;
  origSplit: number | null;
  // Every New column is operator-editable. New Split % pre-fills the three
  // dollar inputs; individual overrides still work after Calculate.
  newSplit: string;
  newLiability: string;
  newPremium: string;
  newRemittence: string;
  reversalOnly: boolean;
  // Dollar inputs snapshotted when Reversal Only is checked, so an accidental
  // check + uncheck doesn't discard what the operator typed.
  cached?: Record<MoneyField, string>;
};

// A split is a percentage of a premium, so 0–100 is the only meaningful range.
// Blur-time only: clamping during typing would rewrite the operator's keystrokes.
const formatPct = (value: string) => {
  const n = toNumOrNull(value);
  if (n === null) return value;
  return Math.min(100, Math.max(0, n)).toFixed(2);
};

// Already-adjusted records can't be adjusted again, so they never get a row.
const isEligible = (r: PolicyManagementRecord) => !r.isAdjustment;

const buildSplitRows = (records: PolicyManagementRecord[]): SplitRow[] =>
  records.filter(isEligible).map((r, i) => ({
    id: r.uniqueIdentifier || `split-${i}`,
    policyHistoryId: r.PolicyHistoryID ?? 0,
    policy: r.PolicyNumber || "---",
    period: r.AccountingPeriod || "---",
    booked: r.bookedRow
      ? r.bookedRow.charAt(0).toUpperCase() + r.bookedRow.slice(1)
      : "—",
    originalLiability: r.LiabilityAmount ?? 0,
    originalPremium: bookedPremium(r),
    originalUWRemittence: bookedRemittance(r),
    origSplit: r.OriginalRemitSplitPct ?? null,
    newSplit: "",
    newLiability: "",
    newPremium: "",
    newRemittence: "",
    reversalOnly: false,
  }));

const applySplit = (row: SplitRow, newSplit: string): SplitRow => {
  const raw = toNumOrNull(newSplit);
  if (raw === null) {
    return {
      ...row,
      newSplit,
      newLiability: "",
      newPremium: "",
      newRemittence: "",
    };
  }

  const pct = Math.min(Math.max(raw, 0), 100);

  const basePremium = rowPremiumBase(row);
  const baseLiability = toNumOrNull(row.newLiability) ?? row.originalLiability;
  return {
    ...row,
    newSplit,
    newLiability: baseLiability.toFixed(2),
    newPremium: basePremium.toFixed(2),
    newRemittence: expectedRemittance(basePremium, pct).toFixed(2),
  };
};

// The premium a row's New Split % is measured against: its own New Premium once
// typed, otherwise the original.
const rowPremiumBase = (r: SplitRow) =>
  toNumOrNull(r.newPremium) ?? r.originalPremium;

// A row is submittable when it carries at least one new dollar amount, or when
// it's flagged Reversal Only.
const rowQualifies = (r: SplitRow) =>
  r.reversalOnly ||
  [r.newLiability, r.newPremium, r.newRemittence].some((v) => v.trim() !== "");

// New UW Remittance must not be greater than its New Premium. Equality (a 100%
// split) is allowed. Reversal-only rows carry no new dollars, so they're exempt.
const remittanceExceedsPremium = (r: SplitRow) => {
  if (r.reversalOnly) return false;
  const remittance = toNumOrNull(r.newRemittence);
  if (remittance === null) return false;
  return remittance > rowPremiumBase(r);
};

// Every New column is independently editable, so an operator can type a split,
// then edit Premium or UW Remittance and leave the three inconsistent. The row
// still submits all three, so the split would no longer describe the amounts.
const splitMismatch = (r: SplitRow) => {
  if (r.reversalOnly) return false;
  const pct = toNumOrNull(r.newSplit);
  const remittance = toNumOrNull(r.newRemittence);
  // Nothing to reconcile until both a split and a remittance are present.
  if (pct === null || remittance === null) return false;
  return isSplitMismatch(pct, rowPremiumBase(r), remittance);
};

export type BulkAdjustChange = {
  recordId: string;
  reversalOnly: boolean;
  newLiability: number | null;
  newPremium: number | null;
  newRemittence: number | null;
  newSplitPct: number | null;
  origSplitPct: number | null;
};

interface BulkAdjustModalProps {
  open: boolean;
  onClose: () => void;
  records: PolicyManagementRecord[];
  onAdjust?: (changes: BulkAdjustChange[], reason: string) => void;
  onRefresh?: () => void;
}

const BulkAdjustModal = ({
  open,
  onClose,
  records,
  onAdjust,
  onRefresh,
}: BulkAdjustModalProps) => {
  const [reason, setReason] = useState("");
  const [bulkSplit, setBulkSplit] = useState("");
  const [splitRows, setSplitRows] = useState<SplitRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showMismatch, setShowMismatch] = useState(false);

  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setReason("");
      setBulkSplit("");
      setIsSaving(false);
      setShowMismatch(false);
      setSplitRows(buildSplitRows(records));
    }
    wasOpen.current = open;
  }, [open, records]);


  const updateRow = (id: string, fn: (row: SplitRow) => SplitRow) => {
    setShowMismatch(false);
    setSplitRows((prev) => prev.map((r) => (r.id === id ? fn(r) : r)));
  };

  // Calculate: fill every row's New Split % with the toolbar value and
  // recompute its dollars. Reversal-only rows are left alone.
  const calcAllSplits = () => {
    if (toNumOrNull(bulkSplit) === null) return;
    setShowMismatch(false);
    setSplitRows((prev) =>
      prev.map((r) => (r.reversalOnly ? r : applySplit(r, bulkSplit))),
    );
  };

  const setRowSplit = (id: string, value: string) =>
    updateRow(id, (r) => applySplit(r, value));

  const setRowMoney = (id: string, field: MoneyField, value: string) =>
    updateRow(id, (r) => ({ ...r, [field]: value }));

  // Reversal Only zeroes out and locks the row's dollar inputs so the intent is
  // unambiguous: this row reverses without a replacement record.
  const toggleReversalOnly = (id: string, checked: boolean) =>
    updateRow(id, (r) =>
      checked
        ? {
            ...r,
            reversalOnly: true,
            cached: {
              newLiability: r.newLiability,
              newPremium: r.newPremium,
              newRemittence: r.newRemittence,
            },
            newLiability: "0.00",
            newPremium: "0.00",
            newRemittence: "0.00",
          }
        : {
            ...r,
            reversalOnly: false,
            cached: undefined,
            newLiability: r.cached?.newLiability ?? "",
            newPremium: r.cached?.newPremium ?? "",
            newRemittence: r.cached?.newRemittence ?? "",
          },
    );

  const invalidRowCount = splitRows.filter(remittanceExceedsPremium).length;
  const canApply = splitRows.some(rowQualifies) && invalidRowCount === 0;

  const mismatchedRows = splitRows.filter(splitMismatch);
  const mismatchRowCount = mismatchedRows.length;

  const selectedRecordsCount = splitRows.length;

  const handleApply = async () => {
    if (!canApply || isSaving) return;

    if (mismatchRowCount > 0) {
      setShowMismatch(true);
      return;
    }

    const submittable = splitRows
      .filter(rowQualifies)
      .filter((r) => r.policyHistoryId > 0);

    if (submittable.length === 0) {
      showToast({ message: BULK_ADJUST_NO_ROWS, type: "info" });
      return;
    }

    const changes: BulkAdjustChange[] = submittable.map((r) => ({
      recordId: r.id,
      reversalOnly: r.reversalOnly,
      newLiability: r.reversalOnly ? null : toNumOrNull(r.newLiability),
      newPremium: r.reversalOnly ? null : toNumOrNull(r.newPremium),
      newRemittence: r.reversalOnly ? null : toNumOrNull(r.newRemittence),
      newSplitPct: toNumOrNull(r.newSplit),
      origSplitPct: r.origSplit,
    }));

    setIsSaving(true);

    const isAdjusted = await bulkAdjustPolicyHistory({
      AdjustmentReason: reason.trim(),
      Adjustments: submittable.map((r) => ({
        PolicyHistoryID: r.policyHistoryId,
        IsReversalOnly: r.reversalOnly,
        ...(r.reversalOnly
          ? {}
          : {
              NewLiabilityAmount: toNumOrNull(r.newLiability),
              NewPremiumAmount: toNumOrNull(r.newPremium),
              NewRetentionAmount: toNumOrNull(r.newRemittence),
            }),
        NewSplitPct: toNumOrNull(r.newSplit),
      })),
    });

    setIsSaving(false);

    if (!isAdjusted) {
      showToast({ message: BULK_ADJUST_ERROR, type: "error" });
      return;
    }

    showToast({
      message: `${submittable.length} record${
        submittable.length === 1 ? "" : "s"
      } ${BULK_ADJUST_SUCCESS_SUFFIX}`,
      type: "success",
    });
    onAdjust?.(changes, reason.trim());
    onClose();
    onRefresh?.();
  };

  const moneyInputProps = (r: SplitRow, field: MoneyField) => ({
    size: "small" as const,
    placeholder: "0.00",
    disabled: r.reversalOnly,
    value: r[field],
    // Premium and UW Remittance are the two sides of both rules, so flag both.
    error:
      (field === "newPremium" || field === "newRemittence") &&
      (remittanceExceedsPremium(r) || (showMismatch && splitMismatch(r))),
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setRowMoney(r.id, field, e.target.value),
  });

  return (
    <StyledDialog open={open}>
      <StyledDialogTitle>
        <IconContainer>
          <PublishedWithChanges />
        </IconContainer>
        <Typography>Apply Bulk Adjustment</Typography>
        <CloseIconContainer>
          <IconButton className="iconButton" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </CloseIconContainer>
      </StyledDialogTitle>

      <StyledDialogContent>
        <Subtitle>
          Adjust <strong>{selectedRecordsCount}</strong> selected closed record
          {selectedRecordsCount === 1 ? "" : "s"}
        </Subtitle>
        <Box>
          <InfoBanner>
            <Info sx={{ color: colors.blue15, fontSize: 18, flexShrink: 0 }} />
            <Box component="span" sx={{ fontSize: fontSize.small }}>
              Enter the new dollar amounts for each record. For each row, a
              reversal will zero out the original plus a new record will carry
              the dollars entered, producing two new{" "}
              <strong>Close Queue</strong> records per source record. Leave a
              row&apos;s inputs blank to skip that record. Accounting Period is
              left blank on all new records and gets assigned on Close.
            </Box>
          </InfoBanner>

          <CalcToolbar>
            <CalcSection>
              <CalcLabel>New Split:</CalcLabel>
              <SplitPctInput
                size="small"
                placeholder="0.00"
                value={bulkSplit}
                onChange={(e) => setBulkSplit(e.target.value)}
                onBlur={(e) => setBulkSplit(formatPct(e.target.value))}
                InputProps={{
                  inputComponent: PercentNumberFormat as any,
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
              />
              <ActionButton
                variant="contained"
                color="primary"
                startIcon={<CalculateIcon />}
                onClick={calcAllSplits}
              >
                Calculate
              </ActionButton>
            </CalcSection>
            <CalcHint component="span">
              Fills <strong>New Split %</strong> for all rows.{" "}
              <strong>New UW Remittance</strong> = Premium × New Split %;{" "}
              <strong>Premium</strong> and <strong>Liability</strong> default to
              the original.
            </CalcHint>
          </CalcToolbar>

          <SplitsTableWrap>
            <SplitsTable>
              <thead>
                <tr>
                  <Th>Policy</Th>
                  <Th>Accounting Period</Th>
                  <Th>Booked Row</Th>
                  <Th>Original Liability</Th>
                  <Th>Original Premium</Th>
                  <Th>Original UW Remittance</Th>
                  <Th>Original Split%</Th>
                  <ThNew>New Split%</ThNew>
                  <ThNew>New Liability</ThNew>
                  <ThNew>New Premium</ThNew>
                  <ThNew>New UW Remittance</ThNew>
                  <ThRev>Reversal Only?</ThRev>
                </tr>
              </thead>
              <tbody>
                {splitRows.map((r) => (
                  <tr key={r.id}>
                    <Td>{r.policy}</Td>
                    <Td>{r.period}</Td>
                    <Td>{r.booked}</Td>
                    <TdNum>
                      <div>{fmtCurrency(r.originalLiability)}</div>
                      <ReversalPreview>
                        {fmtCurrency(-r.originalLiability)}
                      </ReversalPreview>
                    </TdNum>
                    <TdNum>
                      <div>{fmtCurrency(r.originalPremium)}</div>
                      <ReversalPreview>
                        {fmtCurrency(-r.originalPremium)}
                      </ReversalPreview>
                    </TdNum>
                    <TdNum>
                      <div>{fmtCurrency(r.originalUWRemittence)}</div>
                      <ReversalPreview>
                        {fmtCurrency(-r.originalUWRemittence)}
                      </ReversalPreview>
                    </TdNum>
                    <TdNum>
                      {r.origSplit === null ? "—" : fmtPct(r.origSplit)}
                    </TdNum>
                    <TdNew>
                      <SplitPctInput
                        size="small"
                        placeholder="0.00"
                        value={r.newSplit}
                        error={showMismatch && splitMismatch(r)}
                        onChange={(e) => setRowSplit(r.id, e.target.value)}
                        onBlur={(e) =>
                          setRowSplit(r.id, formatPct(e.target.value))
                        }
                        InputProps={{
                          inputComponent: PercentNumberFormat as any,
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        }}
                      />
                    </TdNew>
                    <TdNew>
                      <MoneyInput {...moneyInputProps(r, "newLiability")} />
                    </TdNew>
                    <TdNew>
                      <MoneyInput {...moneyInputProps(r, "newPremium")} />
                    </TdNew>
                    <TdNew>
                      <MoneyInput {...moneyInputProps(r, "newRemittence")} />
                      {remittanceExceedsPremium(r) && (
                        <CellError>
                          {REMITTANCE_OVER_PREMIUM_FIELD_ERROR(
                            rowPremiumBase(r),
                          )}
                        </CellError>
                      )}

                      {showMismatch && splitMismatch(r) && (
                        <CellError>
                          {SPLIT_MISMATCH_FIELD_ERROR(
                            toNumOrNull(r.newSplit) ?? 0,
                            rowPremiumBase(r),
                            expectedRemittance(
                              rowPremiumBase(r),
                              toNumOrNull(r.newSplit) ?? 0,
                            ),
                          )}
                        </CellError>
                      )}
                    </TdNew>
                    <TdRev>
                      <CheckboxInput
                        name={`reversal-only-${r.id}`}
                        checked={r.reversalOnly}
                        onChange={(e) =>
                          toggleReversalOnly(r.id, e.target.checked)
                        }
                      />
                    </TdRev>
                  </tr>
                ))}
              </tbody>
            </SplitsTable>
            {splitRows.length === 0 && (
              <EmptySplits>No records to adjust.</EmptySplits>
            )}
          </SplitsTableWrap>

          {/* Reason section */}
          <NoteSection>
            <NoteHeader>
              <Typography
                sx={{
                  fontSize: fontSize.xsmall1,
                  fontWeight: fontWeight.bold2,
                  color: colors.grey11,
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                }}
              >
                Reason for Adjustments
                <span
                  style={{
                    fontWeight: 400,
                    textTransform: "none",
                    letterSpacing: 0,
                    color: colors.grey11,
                  }}
                >
                  (optional)
                </span>
              </Typography>
            </NoteHeader>
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={6}
              placeholder="Optional shared reason applied to every record in this batch."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </NoteSection>
        </Box>

        <DialogFooter>
          {invalidRowCount > 0 && (
            <FooterError>
              {invalidRowCount} row{invalidRowCount === 1 ? "" : "s"} ha
              {invalidRowCount === 1 ? "s" : "ve"} a UW Remittance greater than
              the New Premium. Correct{" "}
              {invalidRowCount === 1 ? "it" : "them"} to continue.
            </FooterError>
          )}

          {invalidRowCount === 0 && showMismatch && mismatchRowCount > 0 && (
            <FooterError>
              {mismatchRowCount} row{mismatchRowCount === 1 ? "" : "s"} ha
              {mismatchRowCount === 1 ? "s" : "ve"} a New Split % that
              doesn&apos;t match the New Premium and UW Remittance. Correct{" "}
              {mismatchRowCount === 1 ? "it" : "them"} to continue.
            </FooterError>
          )}
          <ActionButton
            variant="outlined"
            color="secondary"
            disabled={isSaving}
            onClick={onClose}
          >
            Cancel
          </ActionButton>
          <ActionButton
            variant="contained"
            color="primary"
            disabled={!canApply || isSaving}
            onClick={handleApply}
          >
            {isSaving ? "Applying..." : "Apply Bulk Adjustment"}
          </ActionButton>
        </DialogFooter>
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default BulkAdjustModal;
